// components/WorkspaceCard.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import LayerCanvas from './LayerCanvas';
import AnnotationCanvas, { AnnotationCanvasHandle } from './AnnotationCanvas';
import { QuickMenuAction, Workspace, WorkspaceVersion } from '#types/types';
import { Rect } from '#types/types';
import SimpleLoader from './ui/SimpleLoader';
import CanvasResizeHandles from './CanvasResizeHandles';
import FrameResizeHandles from './FrameResizeHandles';
import { useKernel } from '@/kernel/Kernel';
import {
  useKernelStore,
} from '@/kernel/kernelStores';
import WorkspaceVersionsPanel from './WorkspaceVersionsPanel';
import {
  WorkspaceTimelineMountGate,
  type WorkspaceTimelineTrack,
  type WorkspaceComposedPlaybackState,
  type WorkspacePlaybackMediaClockHint,
} from './WorkspaceTimelinePanel';
import { buildLiveScreenX, buildLiveScreenY } from '#lib/layer-geometry';
import {
  selectOrderedWorkspaceLayerIdsSignature,
  selectWorkspaceRasterProgress,
} from '#lib/workspace-card-selectors';
import {
  computeVersionHierarchyLabels,
  sortVersionsByTimeline,
} from '#lib/workspace-version-labels';
import { generationAreaCursor, getMaskPenCursor } from '#lib/svg';
import { drawImageWithStudioLayerTransform } from '#lib/studio-layer-transform';
import {
  getProjectSelectedLayersId,
  setProjectIsPanning,
  setProjectDraggedLayerId,
  setProjectIsResizingLayer,
  setProjectPointBeingDragged,
} from '#lib/project-runtime-layer-ui-state';

type ResizeStartType = {
  x: number;
  y: number;
  initialScale: number;
  initialRect?: Rect;
} | null;

interface WorkspaceCardProps {
  projectId: string;
  workspaceId: string;
  mouseDownPos: React.RefObject<{ x: number; y: number }>;
  resizeStart: React.RefObject<ResizeStartType>;
  onDragStart: (type: 'move' | 'resize', layerIds: string[]) => void;
  registerAnnotationCanvasRef: (workspaceId: string, instance: AnnotationCanvasHandle | null) => void;
  onEditDrawingTextLayer?: (layerId: string) => boolean;
  editingDrawingTextLayerId?: string | null;
}

const DEFAULT_PAN_OFFSET = { x: 0, y: 0 } as const;
const EMPTY_RECT: Rect = { x: 0, y: 0, w: 0, h: 0 };
const EMPTY_WORKSPACE_VERSIONS: WorkspaceVersion[] = [];
// While paused (frame-stepping / scrubbing) we seek the video to the exact cursor time.
// This threshold MUST stay well below one timeline frame (1/30s ≈ 33ms). At 0.04 (40ms)
// a single-frame step (~33ms drift) fell under it, so the video never re-seeked: the
// painted frame stalled on the 0→1 step and only refreshed every 2nd step (drift had to
// accumulate past 40ms). 5ms guarantees every frame-step re-seeks while still dedup-ing
// redundant seeks once the video already sits on the target frame.
const PAUSED_VIDEO_SEEK_THRESHOLD_SEC = 0.005;
const EMPTY_COMPOSED_PLAYBACK_STATE: WorkspaceComposedPlaybackState = {
  enabled: false,
  sourceUrl: null,
  isPlaying: false,
  isMuted: false,
  isPreparing: false,
  preparePercent: 0,
  prepareMessage: null,
  tracks: [],
  cursorMs: 0,
  playbackRate: 1,
  playbackStartMs: 0,
  playbackEndMs: 0,
};
const WORKSPACE_RATIO_TOLERANCE = 0.015;
const WORKSPACE_RATIO_PRESETS = [
  { label: '1:1', value: 1, name: { en: 'Square', fr: 'Carré' } },
  { label: '4:3', value: 4 / 3, name: { en: 'Landscape', fr: 'Paysage' } },
  { label: '5:4', value: 5 / 4, name: { en: 'Landscape', fr: 'Paysage' } },
  { label: '16:9', value: 16 / 9, name: { en: 'Widescreen', fr: 'Plein écran' } },
  { label: '3:4', value: 3 / 4, name: { en: 'Portrait', fr: 'Portrait' } },
  { label: '4:5', value: 4 / 5, name: { en: 'Portrait', fr: 'Portrait' } },
  { label: '9:16', value: 9 / 16, name: { en: 'Vertical', fr: 'Vertical' } },
] as const;

const workspaceRatioTranslations = {
  en: {
    ratio: 'Ratio',
    fitGenerationArea: 'Fit generation area',
  },
  fr: {
    ratio: 'Ratio',
    fitGenerationArea: 'Ajuster à la zone de génération',
  },
} as const;

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

const arePlaybackTracksEqual = (
  left: WorkspaceTimelineTrack[],
  right: WorkspaceTimelineTrack[]
): boolean => {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    const a = left[index];
    const b = right[index];
    if (!a || !b) return false;
    if (
      a.layerId !== b.layerId ||
      a.name !== b.name ||
      a.layerType !== b.layerType ||
      a.isLayerVisible !== b.isLayerVisible ||
      a.videoSrc !== b.videoSrc ||
      a.imageSrc !== b.imageSrc ||
      a.audioSrc !== b.audioSrc ||
      a.separatedFromLayerId !== b.separatedFromLayerId ||
      a.audioGain !== b.audioGain ||
      a.mutedInTimeline !== b.mutedInTimeline ||
      a.renderOrder !== b.renderOrder ||
      a.startMs !== b.startMs ||
      a.endMs !== b.endMs ||
      a.sourceInMs !== b.sourceInMs ||
      a.fadeInRatio !== b.fadeInRatio ||
      a.fadeOutRatio !== b.fadeOutRatio ||
      a.rotationDeg !== b.rotationDeg ||
      a.flipX !== b.flipX ||
      a.flipY !== b.flipY ||
      a.opacity !== b.opacity ||
      a.scale !== b.scale ||
      a.rect.x !== b.rect.x ||
      a.rect.y !== b.rect.y ||
      a.rect.w !== b.rect.w ||
      a.rect.h !== b.rect.h
    ) {
      return false;
    }
  }
  return true;
};

const areComposedPlaybackStatesEqual = (
  left: WorkspaceComposedPlaybackState,
  right: WorkspaceComposedPlaybackState
): boolean =>
  left.enabled === right.enabled &&
  left.sourceUrl === right.sourceUrl &&
  left.isPlaying === right.isPlaying &&
  left.isMuted === right.isMuted &&
  left.isPreparing === right.isPreparing &&
  left.preparePercent === right.preparePercent &&
  left.prepareMessage === right.prepareMessage &&
  left.cursorMs === right.cursorMs &&
  left.playbackRate === right.playbackRate &&
  left.playbackStartMs === right.playbackStartMs &&
  left.playbackEndMs === right.playbackEndMs &&
  arePlaybackTracksEqual(left.tracks, right.tracks);

const toFiniteMs = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const formatWorkspaceRatioLabel = (ratio: number | null): string => {
  if (ratio === null) return '\u2014';
  const matchingPreset = WORKSPACE_RATIO_PRESETS.find((preset) => Math.abs(preset.value - ratio) <= WORKSPACE_RATIO_TOLERANCE);
  if (matchingPreset) return matchingPreset.label;
  return ratio.toFixed(2);
};

// frameRect is expressed in workspace world units (~1 MP), NOT export pixels, so
// sizing the compositor backing store to it caps the live preview near 720p even
// when the composition targets 1080p/2K. We keep the frameRect coordinate space for
// drawing (layer rects are workspace-local) but scale the backing store up to the
// export upscale ceiling — 2560 on the longest side, matching
// REFERENCE_CLIP_MAX_PIXELS_UPSCALE (2560×1440) in kernel/useModels.ts — so the
// preview resolves at the real target instead of the world-unit frameRect.
const PREVIEW_TARGET_LONGEST_SIDE_PX = 2560;

interface WorkspaceTimelineCanvasPreviewProps {
  frameRect: Rect;
  tracks: WorkspaceTimelineTrack[];
  playbackState: WorkspaceComposedPlaybackState;
  playbackMediaClockHintRef?: React.MutableRefObject<WorkspacePlaybackMediaClockHint>;
}

const WorkspaceTimelineCanvasPreview: React.FC<WorkspaceTimelineCanvasPreviewProps> = React.memo(({
  frameRect,
  tracks,
  playbackState,
  playbackMediaClockHintRef,
}) => {
  const normalizeMediaSourceForCompare = (src: string | null | undefined): string => {
    if (typeof src !== 'string') return '';
    const trimmed = src.trim();
    if (!trimmed) return '';
    try {
      const url = new URL(trimmed, window.location.origin);
      return `${url.origin}${url.pathname}${url.search}`;
    } catch {
      return trimmed;
    }
  };

  const computeTrackPlaybackOpacityAtMs = (
    track: WorkspaceTimelineTrack,
    timelineMs: number
  ): number => {
    const startMs = Math.max(0, Number.isFinite(track.startMs) ? track.startMs : 0);
    const endMs =
      typeof track.endMs === 'number' && Number.isFinite(track.endMs) && track.endMs > startMs
        ? track.endMs
        : (startMs + 1);
    const durationMs = Math.max(1, endMs - startMs);
    const clampedTimelineMs = clamp(timelineMs, startMs, endMs);
    const localMs = clamp(clampedTimelineMs - startMs, 0, durationMs);
    const fadeInRatioRaw = typeof track.fadeInRatio === 'number' ? track.fadeInRatio : 0;
    const fadeOutRatioRaw = typeof track.fadeOutRatio === 'number' ? track.fadeOutRatio : 0;
    const fadeInDurationMs = durationMs * clamp(fadeInRatioRaw, 0, 1);
    const fadeOutDurationMs = durationMs * clamp(fadeOutRatioRaw, 0, 1);
    let opacity = clamp(typeof track.opacity === 'number' && Number.isFinite(track.opacity) ? track.opacity : 1, 0, 1);
    if (fadeInDurationMs > 0.001) {
      opacity *= clamp(localMs / fadeInDurationMs, 0, 1);
    }
    if (fadeOutDurationMs > 0.001) {
      const fadeOutStartLocalMs = durationMs - fadeOutDurationMs;
      const fadeOutProgress = clamp((localMs - fadeOutStartLocalMs) / fadeOutDurationMs, 0, 1);
      opacity *= clamp(1 - fadeOutProgress, 0, 1);
    }
    return clamp(opacity, 0, 1);
  };

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoElementsRef = useRef<Record<string, HTMLVideoElement>>({});
  const imageElementsRef = useRef<Record<string, HTMLImageElement>>({});
  const targetTimesByLayerIdRef = useRef<Record<string, number>>({});
  const lastSeekAtMsByLayerIdRef = useRef<Record<string, number>>({});
  const startedVideoLayerIdsRef = useRef<Set<string>>(new Set());
  const lastDrawCursorMsRef = useRef<number>(0);
  const playbackClockRef = useRef({
    cursorMs: 0,
    perfMs: 0,
    playbackRate: 1,
    isPlaying: false,
    playbackStartMs: 0,
    playbackEndMs: 0,
  });

  // Logical (frameRect) coordinate space used for all layer drawing.
  const logicalWidth = Math.max(1, frameRect.w);
  const logicalHeight = Math.max(1, frameRect.h);
  // Upscale the backing store to the export target; never downsample a frame that is
  // already larger than the ceiling (previewRenderScale is clamped to >= 1).
  const previewRenderScale = Math.max(
    1,
    PREVIEW_TARGET_LONGEST_SIDE_PX / Math.max(logicalWidth, logicalHeight)
  );
  const canvasWidth = Math.max(1, Math.round(logicalWidth * previewRenderScale));
  const canvasHeight = Math.max(1, Math.round(logicalHeight * previewRenderScale));

  const visualTracks = useMemo(() => {
    return tracks
      .filter((track) => track.isLayerVisible !== false)
      .filter((track) => track.layerType === 'video' || track.layerType === 'image')
      .sort((left, right) => {
        if (left.renderOrder !== right.renderOrder) return left.renderOrder - right.renderOrder;
        return left.layerId.localeCompare(right.layerId);
      });
  }, [tracks]);

  useEffect(() => {
    const playbackRate = Number.isFinite(playbackState.playbackRate) && playbackState.playbackRate > 0
      ? playbackState.playbackRate
      : 1;
    const playbackStartMs = Math.max(0, Number(playbackState.playbackStartMs) || 0);
    const playbackEndMs = Math.max(playbackStartMs + 1, Number(playbackState.playbackEndMs) || playbackStartMs + 1);
    playbackClockRef.current = {
      cursorMs: Math.max(0, Number(playbackState.cursorMs) || 0),
      perfMs: performance.now(),
      playbackRate,
      isPlaying: playbackState.isPlaying,
      playbackStartMs,
      playbackEndMs,
    };
  }, [
    playbackState.cursorMs,
    playbackState.isPlaying,
    playbackState.playbackEndMs,
    playbackState.playbackRate,
    playbackState.playbackStartMs,
  ]);

  useEffect(() => {
    const nextVideoSources = new Map<string, string>();
    const nextImageSources = new Map<string, string>();

    tracks.forEach((track) => {
      if (track.layerType === 'video') {
        const source = typeof track.videoSrc === 'string' ? track.videoSrc.trim() : '';
        if (source.length > 0) {
          nextVideoSources.set(track.layerId, source);
        }
      } else if (track.layerType === 'image') {
        const source = typeof track.imageSrc === 'string' ? track.imageSrc.trim() : '';
        if (source.length > 0) {
          nextImageSources.set(track.layerId, source);
        }
      }
    });

    Object.entries(videoElementsRef.current).forEach(([layerId, element]) => {
      if (nextVideoSources.has(layerId)) return;
      try {
        element.pause();
        element.removeAttribute('src');
        element.load();
      } catch {
        // cleanup best effort
      }
      delete videoElementsRef.current[layerId];
      delete targetTimesByLayerIdRef.current[layerId];
      delete lastSeekAtMsByLayerIdRef.current[layerId];
      startedVideoLayerIdsRef.current.delete(layerId);
    });

    Object.entries(imageElementsRef.current).forEach(([layerId]) => {
      if (nextImageSources.has(layerId)) return;
      delete imageElementsRef.current[layerId];
    });

    nextVideoSources.forEach((source, layerId) => {
      let element = videoElementsRef.current[layerId];
      if (!element) {
        element = document.createElement('video');
        element.preload = 'auto';
        element.crossOrigin = 'anonymous';
        element.muted = true;
        element.playsInline = true;
        element.loop = false;
        videoElementsRef.current[layerId] = element;
      }
      if (
        normalizeMediaSourceForCompare(element.currentSrc || element.src) !==
        normalizeMediaSourceForCompare(source)
      ) {
        element.src = source;
        delete targetTimesByLayerIdRef.current[layerId];
        delete lastSeekAtMsByLayerIdRef.current[layerId];
        try {
          element.load();
        } catch {
          // preload best effort
        }
      }
    });

    nextImageSources.forEach((source, layerId) => {
      let image = imageElementsRef.current[layerId];
      if (!image) {
        image = new Image();
        image.crossOrigin = 'anonymous';
        imageElementsRef.current[layerId] = image;
      }
      if (image.src !== source) {
        image.src = source;
      }
    });
  }, [tracks]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
  }, [canvasHeight, canvasWidth]);

  useEffect(() => {
    if (!playbackState.enabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    let animationFrameId = 0;
    const draw = () => {
      // Draw in the frameRect logical space; the base transform upscales every
      // operation to the high-resolution backing store. setTransform (not scale)
      // resets each frame so the scale never compounds; drawImageWithStudioLayerTransform
      // uses balanced save/restore, so this base transform survives its calls.
      context.setTransform(previewRenderScale, 0, 0, previewRenderScale, 0, 0);
      const clock = playbackClockRef.current;
      const nowMs = performance.now();
      const rawCursorMs = clock.isPlaying
        ? clock.cursorMs + Math.max(0, nowMs - clock.perfMs) * clock.playbackRate
        : clock.cursorMs;
      let cursorMs = clamp(rawCursorMs, clock.playbackStartMs, clock.playbackEndMs);
      const previousDrawCursorMs = Math.max(0, lastDrawCursorMsRef.current || 0);
      const cursorJumpedBackward = cursorMs + 50 < previousDrawCursorMs;
      const getTopActiveVideoTrackAt = (timelineMs: number): WorkspaceTimelineTrack | null => {
        const activeTracks = visualTracks.filter((track) => (
          timelineMs >= track.startMs &&
          timelineMs < (track.endMs ?? Number.MAX_SAFE_INTEGER)
        ));
        return [...activeTracks]
          .filter((track) => track.layerType === 'video')
          .pop() ?? null;
      };
      const writeMediaClockHint = (cursorHintMs: number | null, isWaitingForMedia: boolean) => {
        if (!playbackMediaClockHintRef) return;
        playbackMediaClockHintRef.current = {
          cursorMs: cursorHintMs,
          isWaitingForMedia,
          updatedAtMs: nowMs,
        };
      };

      if (clock.isPlaying) {
        const previousVideoTrack = getTopActiveVideoTrackAt(previousDrawCursorMs);
        const rawVideoTrack = getTopActiveVideoTrackAt(cursorMs);
        const clockVideoTrack = previousVideoTrack ?? rawVideoTrack;
        if (clockVideoTrack) {
          const video = videoElementsRef.current[clockVideoTrack.layerId] ?? null;
          const hasStartedVideoPlayback = startedVideoLayerIdsRef.current.has(clockVideoTrack.layerId);
          const canUseVideoFrame =
            video !== null &&
            video.readyState >= 2 &&
            !video.seeking &&
            video.videoWidth > 0 &&
            video.videoHeight > 0;
          const canUseVideoClock =
            canUseVideoFrame &&
            (!video.paused || video.ended) &&
            Number.isFinite(video.currentTime);
          const videoCursorMs = video !== null && Number.isFinite(video.currentTime)
            ? clockVideoTrack.startMs + video.currentTime * 1000 - clockVideoTrack.sourceInMs
            : null;
          const hasVideoClockAdvanced =
            videoCursorMs !== null &&
            Number.isFinite(videoCursorMs) &&
            videoCursorMs > clockVideoTrack.startMs + 20;
          if (canUseVideoClock || hasVideoClockAdvanced) {
            startedVideoLayerIdsRef.current.add(clockVideoTrack.layerId);
          }
          if (canUseVideoClock) {
            const mediaCursorMs = clamp(
              clockVideoTrack.startMs + video.currentTime * 1000 - clockVideoTrack.sourceInMs,
              clock.playbackStartMs,
              clock.playbackEndMs
            );
            const minimumCursorMs =
              previousDrawCursorMs >= clock.playbackStartMs && previousDrawCursorMs <= clock.playbackEndMs
                ? previousDrawCursorMs
                : clock.cursorMs;
            cursorMs = clamp(Math.max(minimumCursorMs, mediaCursorMs), clock.playbackStartMs, clock.playbackEndMs);
            playbackClockRef.current = {
              ...clock,
              cursorMs,
              perfMs: nowMs,
            };
            writeMediaClockHint(cursorMs, false);
          } else if (hasStartedVideoPlayback) {
            const holdCursorMs = clamp(
              previousDrawCursorMs >= clock.playbackStartMs && previousDrawCursorMs <= clock.playbackEndMs
                ? previousDrawCursorMs
                : clock.cursorMs,
              clock.playbackStartMs,
              clock.playbackEndMs
            );
            cursorMs = holdCursorMs;
            playbackClockRef.current = {
              ...clock,
              cursorMs,
              perfMs: nowMs,
            };
            writeMediaClockHint(cursorMs, true);
          } else {
            writeMediaClockHint(null, false);
          }
        } else {
          writeMediaClockHint(null, false);
        }
      } else {
        writeMediaClockHint(null, false);
      }

      const activeVideoLayerIds = new Set<string>();
      const activeTracksAtCursor = visualTracks.filter((track) => (
        cursorMs >= track.startMs &&
        cursorMs < (track.endMs ?? Number.MAX_SAFE_INTEGER)
      ));
      const topActiveVideoTrack = [...activeTracksAtCursor]
        .filter((track) => track.layerType === 'video')
        .pop() ?? null;
      const topActiveVideoLayerId = topActiveVideoTrack?.layerId ?? null;
      const activeVisualTracks = activeTracksAtCursor.filter((track) => (
        track.layerType !== 'video' || track.layerId === topActiveVideoLayerId
      ));
      const hasActiveVisualTracks = activeVisualTracks.length > 0;
      let didClearForFrame = false;
      let drewVisualForFrame = false;
      const clearFrameToBlack = () => {
        if (didClearForFrame) return;
        // Coordinates are in logical (frameRect) units because of the base transform above.
        context.clearRect(0, 0, logicalWidth, logicalHeight);
        context.fillStyle = '#000000';
        context.fillRect(0, 0, logicalWidth, logicalHeight);
        didClearForFrame = true;
      };

      if (!hasActiveVisualTracks) {
        clearFrameToBlack();
      }

      activeVisualTracks.forEach((track) => {
        const baseRectW = Number.isFinite(track.rect?.w) ? Math.max(0, Number(track.rect.w)) : 0;
        const baseRectH = Number.isFinite(track.rect?.h) ? Math.max(0, Number(track.rect.h)) : 0;
        if (baseRectW <= 0 || baseRectH <= 0) return;
        // Match LayerCanvas geometry exactly:
        // rect.{x,y,w,h} are already workspace-local display coordinates.
        const drawW = baseRectW;
        const drawH = baseRectH;
        const drawX = Number.isFinite(track.rect?.x) ? Number(track.rect.x) : 0;
        const drawY = Number.isFinite(track.rect?.y) ? Number(track.rect.y) : 0;

        const trackOpacity = computeTrackPlaybackOpacityAtMs(track, cursorMs);
        if (trackOpacity <= 0.001) return;

        if (track.layerType === 'image') {
          const image = imageElementsRef.current[track.layerId];
          if (!image || !image.complete || image.naturalWidth <= 0 || image.naturalHeight <= 0) return;
          try {
            clearFrameToBlack();
            context.save();
            context.globalAlpha = trackOpacity;
            drawImageWithStudioLayerTransform(
              context,
              image,
              drawX,
              drawY,
              drawW,
              drawH,
              {
                rotationDeg: track.rotationDeg ?? 0,
                flipX: track.flipX === true,
                flipY: track.flipY === true,
              }
            );
            context.restore();
            drewVisualForFrame = true;
          } catch {
            // ignore frame draw failure for this layer
          }
          return;
        }

        const video = videoElementsRef.current[track.layerId];
        if (!video) return;
        activeVideoLayerIds.add(track.layerId);
        video.playbackRate = clock.playbackRate;
        const targetTimeSec = Math.max(0, (cursorMs - track.startMs + track.sourceInMs) / 1000);
        const videoDriftSec = Math.abs(video.currentTime - targetTimeSec);
        const nowMs = performance.now();
        const lastSeekAtMs = lastSeekAtMsByLayerIdRef.current[track.layerId] ?? 0;
        const hasSyncedTargetTime = Number.isFinite(targetTimesByLayerIdRef.current[track.layerId]);
        const canSeekNow = (nowMs - lastSeekAtMs) > 220;
        const shouldSeek = !clock.isPlaying
          ? videoDriftSec > PAUSED_VIDEO_SEEK_THRESHOLD_SEC
          : (
              cursorJumpedBackward ||
              video.ended ||
              !hasSyncedTargetTime ||
              (videoDriftSec > 0.16 && canSeekNow)
            );
        if (
          video.readyState >= 1 &&
          !video.seeking &&
          shouldSeek &&
          Number.isFinite(targetTimeSec)
        ) {
          try {
            video.currentTime = targetTimeSec;
            targetTimesByLayerIdRef.current[track.layerId] = targetTimeSec;
            lastSeekAtMsByLayerIdRef.current[track.layerId] = nowMs;
          } catch {
            // seek best effort
          }
        }
        if (clock.isPlaying) {
          if (video.paused) {
            void video.play().catch(() => undefined);
          }
        } else if (!video.paused) {
          video.pause();
        }
        if (
          video.readyState >= 2 &&
          !video.seeking &&
          video.videoWidth > 0 &&
          video.videoHeight > 0
        ) {
          try {
            clearFrameToBlack();
            context.save();
            context.globalAlpha = trackOpacity;
            drawImageWithStudioLayerTransform(
              context,
              video,
              drawX,
              drawY,
              drawW,
              drawH,
              {
                rotationDeg: track.rotationDeg ?? 0,
                flipX: track.flipX === true,
                flipY: track.flipY === true,
              }
            );
            context.restore();
            drewVisualForFrame = true;
          } catch {
            // ignore frame draw failure for this layer
          }
        }
      });

      // If tracks are active but none were drawable this RAF tick, keep previous frame
      // instead of flashing to black.
      if (!drewVisualForFrame && !didClearForFrame && !hasActiveVisualTracks) {
        clearFrameToBlack();
      }

      lastDrawCursorMsRef.current = cursorMs;

      Object.entries(videoElementsRef.current).forEach(([layerId, video]) => {
        if (activeVideoLayerIds.has(layerId)) return;
        if (!video.paused) {
          video.pause();
        }
        delete targetTimesByLayerIdRef.current[layerId];
        delete lastSeekAtMsByLayerIdRef.current[layerId];
        startedVideoLayerIdsRef.current.delete(layerId);
      });

      animationFrameId = window.requestAnimationFrame(draw);
    };

    animationFrameId = window.requestAnimationFrame(draw);
    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [
    frameRect.x,
    frameRect.y,
    logicalHeight,
    logicalWidth,
    playbackMediaClockHintRef,
    playbackState.enabled,
    previewRenderScale,
    visualTracks,
  ]);

  useEffect(() => {
    return () => {
      Object.values(videoElementsRef.current).forEach((video) => {
        try {
          video.pause();
          video.removeAttribute('src');
          video.load();
        } catch {
          // cleanup best effort
        }
      });
      videoElementsRef.current = {};
      imageElementsRef.current = {};
      targetTimesByLayerIdRef.current = {};
      lastSeekAtMsByLayerIdRef.current = {};
      startedVideoLayerIdsRef.current.clear();
      lastDrawCursorMsRef.current = 0;
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={canvasWidth}
      height={canvasHeight}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        backgroundColor: '#000000',
        pointerEvents: 'none',
        zIndex: 3,
      }}
    />
  );
});

interface WorkspaceVersionsPanelContainerProps {
  projectId: string;
  workspaceId: string;
  isActive: boolean;
  composedPlaybackVideoRef?: React.RefObject<HTMLVideoElement | null>;
  playbackMediaClockHintRef?: React.MutableRefObject<WorkspacePlaybackMediaClockHint>;
  onComposedPlaybackStateChange?: (state: WorkspaceComposedPlaybackState) => void;
}

const WorkspaceVersionsPanelContainer: React.FC<WorkspaceVersionsPanelContainerProps> = React.memo(({
  projectId,
  workspaceId,
  isActive,
  composedPlaybackVideoRef,
  playbackMediaClockHintRef,
  onComposedPlaybackStateChange,
}) => {
  const selectedWorkspace = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId] ?? null
  );

  const fallbackWorkspace = useMemo<Workspace>(() => ({
    id: workspaceId,
    projectId,
    name: workspaceId,
    layerIds: [],
    activeVersionId: null,
    generationRect: EMPTY_RECT,
    frameRect: EMPTY_RECT,
    annotationMask: null,
    annotationMaskVersionId: null,
    annotationsEnabled: true,
    annotationOpaque: false,
    expanded: true,
    locked: true,
    visible: false,
    aspectRatio: 1,
    validatedVersionId: null,
    versionFilterMode: 'PRIMARY',
    timelineDurationMs: null,
    timelineFps: null,
    preferVersionPanelOverTimeline: false,
    timelineCursorMs: 0,
  }), [workspaceId, projectId]);

  const workspace = useMemo<Workspace>(() => {
    if (!selectedWorkspace) return fallbackWorkspace;
    return {
      ...fallbackWorkspace,
      ...selectedWorkspace,
      id: selectedWorkspace.id || fallbackWorkspace.id,
      name: selectedWorkspace.name || fallbackWorkspace.name,
      layerIds: Array.isArray(selectedWorkspace.layerIds) ? selectedWorkspace.layerIds : [],
      generationRect: selectedWorkspace.generationRect ?? fallbackWorkspace.generationRect,
      frameRect: selectedWorkspace.frameRect ?? fallbackWorkspace.frameRect,
      lastMaskUrl: selectedWorkspace.lastMaskUrl ?? undefined,
      annotationMask: selectedWorkspace.annotationMask ?? null,
      annotationMaskVersionId:
        typeof selectedWorkspace.annotationMaskVersionId === 'string'
          ? selectedWorkspace.annotationMaskVersionId
          : null,
      cameraState: selectedWorkspace.cameraState ?? undefined,
      expanded: selectedWorkspace.expanded !== false,
      locked: selectedWorkspace.locked === true,
      visible: selectedWorkspace.visible !== false,
      aspectRatio: typeof selectedWorkspace.aspectRatio === 'number' ? selectedWorkspace.aspectRatio : null,
      validatedVersionId:
        typeof selectedWorkspace.validatedVersionId === 'string' ? selectedWorkspace.validatedVersionId : null,
      activeVersionId:
        typeof selectedWorkspace.activeVersionId === 'string' ? selectedWorkspace.activeVersionId : null,
      versionFilterMode: selectedWorkspace.versionFilterMode === 'ALL' ? 'ALL' : 'PRIMARY',
      timelineDurationMs: toFiniteMs(selectedWorkspace.timelineDurationMs),
      timelineFps:
        typeof selectedWorkspace.timelineFps === 'number' && Number.isFinite(selectedWorkspace.timelineFps)
          ? selectedWorkspace.timelineFps
          : null,
      preferVersionPanelOverTimeline: selectedWorkspace.preferVersionPanelOverTimeline === true,
      timelineCursorMs: Math.max(0, toFiniteMs(selectedWorkspace.timelineCursorMs) ?? 0),
    };
  }, [fallbackWorkspace, selectedWorkspace]);

  return (
    <WorkspaceTimelineMountGate
      projectId={projectId}
      workspaceId={workspaceId}
      isActive={isActive}
      composedPlaybackVideoRef={composedPlaybackVideoRef}
      playbackMediaClockHintRef={playbackMediaClockHintRef}
      onComposedPlaybackStateChange={onComposedPlaybackStateChange}
    >
      {({ shouldRenderVersionsPanel }) => (
        shouldRenderVersionsPanel ? (
          <WorkspaceVersionsPanel
            projectId={projectId}
            workspace={workspace}
            isActive={isActive}
            renderVersionStrip={true}
          />
        ) : null
      )}
    </WorkspaceTimelineMountGate>
  );
});

WorkspaceVersionsPanelContainer.displayName = 'WorkspaceVersionsPanelContainer';

interface WorkspaceLayerCanvasStackProps {
  workspaceId: string;
  workspaceVisible: boolean;
  workspaceLocked: boolean;
  editingDrawingTextLayerId: string | null;
  handleLayerMouseDown: (event: React.MouseEvent<HTMLDivElement>, layerId: string) => void;
  handleResizeHandleMouseDown: (
    event: React.MouseEvent,
    layerId: string,
    corner: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'top' | 'bottom' | 'left' | 'right'
  ) => void;
  onEditDrawingTextLayer?: (layerId: string) => boolean;
  resizeHandlesPortalHostRef: React.RefObject<HTMLDivElement | null>;
  suppressForComposedPlayback: boolean;
}

const WorkspaceLayerCanvasStack = React.memo(({
  workspaceId,
  workspaceVisible,
  workspaceLocked,
  editingDrawingTextLayerId,
  handleLayerMouseDown,
  handleResizeHandleMouseDown,
  onEditDrawingTextLayer,
  resizeHandlesPortalHostRef,
  suppressForComposedPlayback,
}: WorkspaceLayerCanvasStackProps) => {
  const orderedWorkspaceLayerIdsSignature = useKernelStore((state) => (
    selectOrderedWorkspaceLayerIdsSignature(state, workspaceId)
  ));
  const orderedWorkspaceLayerIds = useMemo(() => {
    if (!orderedWorkspaceLayerIdsSignature) return [];
    return orderedWorkspaceLayerIdsSignature.split(',');
  }, [orderedWorkspaceLayerIdsSignature]);

  return (
    <>
      {orderedWorkspaceLayerIds
        .filter((layerId) => layerId !== editingDrawingTextLayerId)
        .map((layerId) => (
          <LayerCanvas
            key={layerId}
            layerId={layerId}
            isWorkspaceVisible={workspaceVisible}
            handleLayerMouseDown={handleLayerMouseDown}
            onEditDrawingTextLayer={onEditDrawingTextLayer}
            isWorkspaceLocked={workspaceLocked}
            workspaceId={workspaceId}
            handleResizeHandleMouseDown={handleResizeHandleMouseDown}
            resizeHandlesPortalHostRef={resizeHandlesPortalHostRef}
            suppressForComposedPlayback={suppressForComposedPlayback}
          />
        ))}
    </>
  );
});

WorkspaceLayerCanvasStack.displayName = 'WorkspaceLayerCanvasStack';

interface WorkspaceLoadingOverlayProps {
  workspaceId: string;
}

const WorkspaceLoadingOverlay = React.memo(({
  workspaceId,
}: WorkspaceLoadingOverlayProps) => {
  const rasterProgress = useKernelStore((state) => selectWorkspaceRasterProgress(state, workspaceId));
  const isImportingAssets = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.runtime?.isImportingAssets === true
  );
  const isMountingLayers = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.runtime?.isMountingLayers === true
  );
  if (!rasterProgress && !isImportingAssets && !isMountingLayers) return null;

  return (
    <div
      className={isImportingAssets ? 'loading-anim-bg' : 'bg-black/45'}
      style={{
        position: 'absolute',
        inset: 0,
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10,
        borderRadius: 'inherit',
      }}
    >
      <SimpleLoader message={rasterProgress || undefined} />
    </div>
  );
});

WorkspaceLoadingOverlay.displayName = 'WorkspaceLoadingOverlay';

const WorkspaceCard = React.memo(({
  projectId,
  workspaceId,
  mouseDownPos,
  resizeStart,
  onDragStart,
  registerAnnotationCanvasRef,
  onEditDrawingTextLayer,
  editingDrawingTextLayerId = null,
}: WorkspaceCardProps) => {
  const { kernel } = useKernel();
  const language = useKernelStore((state) => state.language);
  const languageKey = language === 'fr' ? 'fr' : 'en';
  const ratioCopy = workspaceRatioTranslations[languageKey];
  
  const isActive = useKernelStore(
    (state) => {
      const runtime = state.inventory.Project.registry[projectId]?.runtime;
      const activeWorkspaceId =
        typeof runtime?.UIWorkspaceId === 'string' ? runtime.UIWorkspaceId.trim() : '';
      return activeWorkspaceId === workspaceId;
    }
  );
  const hasActiveTool = useKernelStore((state) => state.activeTool !== null);
  const activeTool = useKernelStore((state) => state.activeTool);
  const workspaceName = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.name ?? workspaceId
  );
  const workspaceVisible = useKernelStore((state) => {
    const value = state.inventory.StudioWorkspace.registry[workspaceId]?.visible;
    return value !== false;
  });
  const workspaceLocked = useKernelStore((state) => {
    const value = state.inventory.StudioWorkspace.registry[workspaceId]?.locked;
    return value === true;
  });
  const workspaceFrameRect = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.frameRect ?? EMPTY_RECT
  );
  const workspaceVersions = useKernelStore((state) => {
    const versions = state.inventory.StudioWorkspace.registry[workspaceId]?.versions;
    return Array.isArray(versions) ? versions : EMPTY_WORKSPACE_VERSIONS;
  });
  const workspaceValidatedVersionId = useKernelStore((state) => {
    const value = state.inventory.StudioWorkspace.registry[workspaceId]?.validatedVersionId;
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
  });
  const workspaceActiveVersionId = useKernelStore((state) => {
    const value = state.inventory.StudioWorkspace.registry[workspaceId]?.activeVersionId;
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
  });
  const workspaceAnnotationsEnabled = useKernelStore((state) => {
    const value = state.inventory.StudioWorkspace.registry[workspaceId]?.annotationsEnabled;
    return value !== false;
  });
  // The Mask Pen tool reuses AnnotationCanvas, so mount it whenever that tool is active —
  // independent of the per-workspace annotation toggle.
  const maskPenActive = useKernelStore((state) => state.activeTool === 'maskPen');
  const maskPenMode = useKernelStore((state) => state.maskPenMode);
  // Keep the mask overlay mounted (visible + editable with the Select tool) whenever shapes exist,
  // even when annotations are disabled.
  const hasInpaintMask = useKernelStore((state) => (state.inpaintMaskShapes[workspaceId]?.length ?? 0) > 0);
  const workspaceLayerCount = useKernelStore((state) => {
    const layerIds = state.inventory.StudioWorkspace.registry[workspaceId]?.layerIds;
    return Array.isArray(layerIds) ? layerIds.length : 0;
  });
  const workspaceBackgroundColor = useKernelStore((state) => state.inventory.StudioWorkspace.registry[workspaceId]?.backgroundColor);
  // Illustrator's artboard is white paper on the pasteboard; a Vector project's frame reads the same way.
  const projectIsVector = useKernelStore((state) => state.inventory.Project.registry[projectId]?.projectType === 'VECTOR');
  const developSessionForWorkspace = useKernelStore((state) => {
    const session = state.inventory.Project.registry[projectId]?.runtime?.developSession;
    return session?.workspaceId === workspaceId ? session : null;
  });
  const developApplyPreviewUrl = typeof developSessionForWorkspace?.applyPreviewUrl === 'string'
    ? developSessionForWorkspace.applyPreviewUrl
    : null;
  const developPreviewUrl = developApplyPreviewUrl || (
    typeof developSessionForWorkspace?.previewUrl === 'string'
      ? developSessionForWorkspace.previewUrl
      : null
  );
  const isDevelopApplying = developSessionForWorkspace?.isApplying === true;
  const isAltKeyPressed = useKernelStore((state) => state.isAltKeyPressed === true);
  const zoomLevel = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    const value = runtime?.zoomLevel;
    return typeof value === 'number' && Number.isFinite(value) ? value : 1;
  });
  const panOffsetX = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    const value = runtime?.panOffset;
    if (
      value &&
      typeof value === 'object' &&
      Number.isFinite((value as { x?: unknown }).x)
    ) {
      return (value as { x: number }).x;
    }
    return DEFAULT_PAN_OFFSET.x;
  });
  const panOffsetY = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    const value = runtime?.panOffset;
    if (
      value &&
      typeof value === 'object' &&
      Number.isFinite((value as { y?: unknown }).y)
    ) {
      return (value as { y: number }).y;
    }
    return DEFAULT_PAN_OFFSET.y;
  });
  
  // FIX: Ne retourne l'objet frameResizeState QUE s'il concerne ce workspace précis
  const frameResizeState = useKernelStore((state) => {
    const workspace = state.inventory.StudioWorkspace.registry[workspaceId];
    const fs = workspace?.frameResizeState;
    return fs && fs.workspaceId === workspaceId ? fs : null;
  });
  
  const isNativeLayerCutActive = useKernelStore((state) => state.nativeLayerCutSession !== null);

  const treeFocusVersionId = useMemo(() => {
    const validatedId = typeof workspaceValidatedVersionId === 'string' ? workspaceValidatedVersionId.trim() : '';
    if (validatedId && workspaceVersions.some((version) => version.id === validatedId)) {
      return validatedId;
    }

    const oldestVersion = workspaceVersions.slice().sort(sortVersionsByTimeline)[0];
    return oldestVersion?.id ?? null;
  }, [workspaceValidatedVersionId, workspaceVersions]);

  const versionDisplayLabelById = useMemo(() => {
    return computeVersionHierarchyLabels(workspaceVersions, treeFocusVersionId);
  }, [workspaceVersions, treeFocusVersionId]);

  const observedVersionId = useMemo(() => {
    const canonicalVersionId = workspaceActiveVersionId ?? treeFocusVersionId ?? null;
    if (!canonicalVersionId) return null;

    if (!isAltKeyPressed || !isActive || isNativeLayerCutActive) {
      return canonicalVersionId;
    }

    const canonicalVersion = workspaceVersions.find((version) => version.id === canonicalVersionId);
    if (!canonicalVersion?.parentId) {
      return canonicalVersionId;
    }
    const parentExists = workspaceVersions.some((version) => version.id === canonicalVersion.parentId);
    return parentExists ? canonicalVersion.parentId : canonicalVersionId;
  }, [
    workspaceActiveVersionId,
    treeFocusVersionId,
    isAltKeyPressed,
    isActive,
    isNativeLayerCutActive,
    workspaceVersions,
  ]);

  const observedVersionLabel = useMemo(() => {
    if (!observedVersionId) return null;
    return versionDisplayLabelById.get(observedVersionId) ?? null;
  }, [observedVersionId, versionDisplayLabelById]);

  const workspaceFrameRatio = useMemo(() => {
    const width = workspaceFrameRect.w;
    const height = workspaceFrameRect.h;
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
    return width / height;
  }, [workspaceFrameRect.w, workspaceFrameRect.h]);

  const workspaceRatioText = useMemo(() => formatWorkspaceRatioLabel(workspaceFrameRatio), [workspaceFrameRatio]);

  const resizeHandlesPortalHostRef = useRef<HTMLDivElement | null>(null);
  const composedPlaybackVideoRef = useRef<HTMLVideoElement | null>(null);
  const playbackMediaClockHintRef = useRef<WorkspacePlaybackMediaClockHint>({
    cursorMs: null,
    isWaitingForMedia: false,
    updatedAtMs: 0,
  });
  const [composedPlaybackState, setComposedPlaybackState] = useState<WorkspaceComposedPlaybackState>(
    EMPTY_COMPOSED_PLAYBACK_STATE
  );
  const [isLayerStackMounted, setIsLayerStackMounted] = useState(() => workspaceLayerCount === 0);
  const [isEditingWorkspaceName, setIsEditingWorkspaceName] = useState(false);
  const [workspaceNameDraft, setWorkspaceNameDraft] = useState(workspaceName);
  const workspaceNameInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    playbackMediaClockHintRef.current = {
      cursorMs: null,
      isWaitingForMedia: false,
      updatedAtMs: 0,
    };
    setComposedPlaybackState(EMPTY_COMPOSED_PLAYBACK_STATE);
  }, [workspaceId]);
  useEffect(() => {
    if (isLayerStackMounted) return;
    kernel.current?.patchEntityRuntimeState('StudioWorkspace', workspaceId, { isMountingLayers: true });
    // After a discrete event (the eye click) React runs this effect before the browser paints,
    // and a frame callback still precedes that frame's paint: only the timeout it queues lands
    // after the empty frame and its loader are on screen.
    let timeoutId = 0;
    const frameId = window.requestAnimationFrame(() => {
      timeoutId = window.setTimeout(() => setIsLayerStackMounted(true), 0);
    });
    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      kernel.current?.patchEntityRuntimeState('StudioWorkspace', workspaceId, { isMountingLayers: undefined });
    };
  }, [workspaceId]);
  useEffect(() => {
    if (!isLayerStackMounted) return;
    const frameId = window.requestAnimationFrame(() => {
      kernel.current?.patchEntityRuntimeState('StudioWorkspace', workspaceId, { isMountingLayers: undefined });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [isLayerStackMounted, workspaceId]);
  useEffect(() => {
    if (!isEditingWorkspaceName) {
      setWorkspaceNameDraft(workspaceName);
    }
  }, [workspaceName, isEditingWorkspaceName]);
  useEffect(() => {
    if (!isEditingWorkspaceName) return;
    const input = workspaceNameInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [isEditingWorkspaceName]);

  const layersContainerStyle = useMemo(() => {
    if (frameResizeState?.isResizing && frameResizeState.initialRect) {
      const deltaX = workspaceFrameRect.x - frameResizeState.initialRect.x;
      const deltaY = workspaceFrameRect.y - frameResizeState.initialRect.y;
      return {
        transform: `translate(${-deltaX}px, ${-deltaY}px)`,
        transition: 'none',
      };
    }
    return {};
  }, [workspaceFrameRect.x, workspaceFrameRect.y, frameResizeState]);

  const handleResizeHandleMouseDown = useCallback((event: React.MouseEvent, layerId: string, corner: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'top' | 'bottom' | 'left' | 'right') => {
    if (!kernel.current) return;
    const activeTool = kernel.current.getActiveTool();
    if (activeTool === 'scissors' || activeTool === 'draw' || activeTool === 'canvasControl') return;
    event.stopPropagation();
    const layer = kernel.current.getLayerRegistry()[layerId];
    if (layer) {
      onDragStart('resize', [layerId]);
      setProjectIsResizingLayer(kernel.current, projectId, true);
      setProjectDraggedLayerId(kernel.current, projectId, layerId);
      setProjectPointBeingDragged(kernel.current, projectId, { layerId, pointName: corner });
      resizeStart.current = {
        x: event.clientX,
        y: event.clientY,
        initialScale: layer.scale || 1,
        initialRect: layer.rect,
      };
    }
  }, [kernel, resizeStart, onDragStart, projectId]);

  const handleLayerMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>, layerId: string) => {
    if (!kernel.current) return;
    const activeTool = kernel.current.getActiveTool();
    if (activeTool === 'scissors' || activeTool === 'draw' || activeTool === 'canvasControl') return;
    kernel.current.canvasContainerRef.current?.focus({ preventScroll: true });
    const clickedLayer = kernel.current.getLayerRegistry()[layerId];
    if (!clickedLayer) {
      return;
    }

    if (clickedLayer.locked) {
      return;
    }

    if (event.button !== 0) {
      return;
    }

    event.stopPropagation();
    const clickX = event.clientX;
    const clickY = event.clientY;

    const selectedLayerIds = getProjectSelectedLayersId(kernel.current, projectId);
    const isSelected = selectedLayerIds.includes(layerId);
    if (event.shiftKey) {
      kernel.current.selectLayer(projectId, layerId, true);
      return;
    }
    if (!isSelected) {
      kernel.current.selectLayer(projectId, layerId, false);
    }
    const layersToDragIds = isSelected ? selectedLayerIds : [layerId];
    onDragStart('move', layersToDragIds);

    setProjectDraggedLayerId(kernel.current, projectId, layerId);
    setProjectIsPanning(kernel.current, projectId, true);
    mouseDownPos.current = { x: clickX, y: clickY };
    document.body.style.cursor = 'grab';
  }, [kernel, mouseDownPos, onDragStart, projectId]);

  const workspaceOverlayPortalHost = kernel.current?.canvasContainerRef.current ?? null;
  const observedVersionText = observedVersionLabel ? `Version ${observedVersionLabel}` : null;
  const isTimelineCanvasCompositorActive = composedPlaybackState.enabled;
  const isComposedPlaybackActive =
    composedPlaybackState.enabled &&
    typeof composedPlaybackState.sourceUrl === 'string' &&
    composedPlaybackState.sourceUrl.length > 0;
  const isBackendComposedPlaybackActive =
    isComposedPlaybackActive && !isTimelineCanvasCompositorActive;
  const isComposedPlaybackPreparing = composedPlaybackState.isPreparing;

  const handleComposedPlaybackStateChange = useCallback((nextState: WorkspaceComposedPlaybackState) => {
    setComposedPlaybackState((previousState) => {
      let candidateState = nextState;
      if (!nextState.enabled || !nextState.isPlaying || !previousState.enabled || !previousState.isPlaying) {
        candidateState = nextState;
      } else {
        const prevCursorMs = Math.max(0, Number(previousState.cursorMs) || 0);
        const nextCursorRawMs = Math.max(0, Number(nextState.cursorMs) || 0);
        const playbackStartMs = Math.max(0, Number(nextState.playbackStartMs) || 0);
        const playbackEndMs = Math.max(playbackStartMs + 1, Number(nextState.playbackEndMs) || playbackStartMs + 1);
        const playbackRangeMs = Math.max(1, playbackEndMs - playbackStartMs);
        const backwardJumpMs = prevCursorMs - nextCursorRawMs;
        const isSignificantBackwardJump =
          backwardJumpMs > Math.max(120, playbackRangeMs * 0.15);
        const isLoopWrap =
          isSignificantBackwardJump &&
          prevCursorMs >= playbackStartMs + playbackRangeMs * 0.75 &&
          nextCursorRawMs <= playbackStartMs + playbackRangeMs * 0.35;
        if (isLoopWrap) {
          candidateState = nextState;
        } else {
          const monotonicCursorMs = Math.max(prevCursorMs, nextCursorRawMs);
          if (Math.abs(monotonicCursorMs - nextCursorRawMs) < 0.5) {
            candidateState = nextState;
          } else {
            candidateState = {
              ...nextState,
              cursorMs: monotonicCursorMs,
            };
          }
        }
      }
      if (areComposedPlaybackStatesEqual(previousState, candidateState)) {
        return previousState;
      }
      return candidateState;
    });
  }, []);

  const workspaceFrameStyle = useMemo(() => {
    const borderColor = isActive ? '#ECECEC' : '#262626';

    return {
      position: 'absolute' as const,
      left: 0,
      top: 0,
      width: workspaceFrameRect.w,
      height: workspaceFrameRect.h,
      transform: `translate(${workspaceFrameRect.x}px, ${workspaceFrameRect.y}px)`,
      transformOrigin: '0 0',
      overflow: 'hidden',
      // Functional, not style: the starfield showing through is what tells an empty workspace apart.
      backgroundColor: workspaceBackgroundColor ?? (projectIsVector ? '#FFFFFF' : workspaceLayerCount === 0 ? 'transparent' : '#000000'),
      boxShadow: `0 0 0 calc(2px / min(200, var(--zoom-level, 1))) ${borderColor}`,
      cursor: activeTool === 'maskPen'
        ? getMaskPenCursor(maskPenMode)
        : activeTool === 'canvasControl'
          ? generationAreaCursor
          : activeTool === 'draw'
            ? 'crosshair'
            : (workspaceLocked ? 'default' : (activeTool === 'scissors' ? 'default' : (!hasActiveTool ? 'move' : 'default'))),
      pointerEvents: 'auto' as const,
      borderRadius: 'calc(25px / var(--zoom-level, 1))',
    };
  }, [workspaceFrameRect, workspaceLocked, isActive, hasActiveTool, workspaceLayerCount, workspaceBackgroundColor, activeTool, maskPenMode]);

  const handleWorkspaceBadgeMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);
  const handleWorkspaceNameInputMouseDown = useCallback((event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
  }, []);
  const handleWorkspaceNameInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setWorkspaceNameDraft(event.currentTarget.value);
  }, []);
  // Inline callback ref: React invokes the previous one with null on every render, and
  // registerAnnotationCanvasRef deletes the entry on null. WorkspaceCard re-renders on
  // every pan/zoom frame, so the workspace disappeared from annotationCanvasRefs during
  // each of those frames.
  const attachAnnotationCanvasRef = useCallback((instance: AnnotationCanvasHandle | null) => {
    registerAnnotationCanvasRef(workspaceId, instance);
  }, [registerAnnotationCanvasRef, workspaceId]);
  const commitWorkspaceNameRename = useCallback((nextRawName: string) => {
    const nextNameTrimmed = nextRawName.trim();
    const canonicalNameFromStore = kernel.current?.getInventory().StudioWorkspace.registry[workspaceId]?.name;
    const canonicalName =
      typeof canonicalNameFromStore === 'string' && canonicalNameFromStore.trim().length > 0
        ? canonicalNameFromStore
        : workspaceId;

    if (!nextNameTrimmed) {
      setWorkspaceNameDraft(canonicalName);
      setIsEditingWorkspaceName(false);
      return;
    }

    if (kernel.current) {
      kernel.current.renameWorkspace(workspaceId, nextNameTrimmed);
    }
    setWorkspaceNameDraft(nextNameTrimmed);
    setIsEditingWorkspaceName(false);
  }, [kernel, workspaceId]);
  const handleWorkspaceNameClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (isEditingWorkspaceName) return;
    const canonicalNameFromStore = kernel.current?.getInventory().StudioWorkspace.registry[workspaceId]?.name;
    setWorkspaceNameDraft(
      typeof canonicalNameFromStore === 'string' && canonicalNameFromStore.trim().length > 0
        ? canonicalNameFromStore
        : workspaceId
    );
    setIsEditingWorkspaceName(true);
  }, [isEditingWorkspaceName, kernel, workspaceId]);
  const handleWorkspaceNameInputBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    event.stopPropagation();
    commitWorkspaceNameRename(event.currentTarget.value);
  }, [commitWorkspaceNameRename]);
  const handleWorkspaceNameInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      commitWorkspaceNameRename(event.currentTarget.value);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      const canonicalNameFromStore = kernel.current?.getInventory().StudioWorkspace.registry[workspaceId]?.name;
      const canonicalName =
        typeof canonicalNameFromStore === 'string' && canonicalNameFromStore.trim().length > 0
          ? canonicalNameFromStore
          : workspaceId;
      setWorkspaceNameDraft(canonicalName);
      setIsEditingWorkspaceName(false);
    }
  }, [commitWorkspaceNameRename, kernel, workspaceId]);

  const openWorkspaceRatioMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const actions: QuickMenuAction[] = [
      {
        label: ratioCopy.fitGenerationArea,
        onClick: () => {
          if (!projectId) return;
          kernel.current?.fitWorkspaceFrameToGenerationArea(projectId, workspaceId);
        },
      },
      ...WORKSPACE_RATIO_PRESETS.map((preset): QuickMenuAction => ({
        label: `${preset.label} (${preset.name[languageKey]})`,
        selected: workspaceFrameRatio !== null && Math.abs(workspaceFrameRatio - preset.value) <= WORKSPACE_RATIO_TOLERANCE,
        onClick: () => {
          if (!projectId) return;
          kernel.current?.forceFrameRatioOnContent?.(projectId, workspaceId, preset.value);
        },
      })),
    ];

    kernel.current?.setMenuData({
      position: { x: event.clientX, y: event.clientY },
      actions,
    });
  }, [kernel, languageKey, projectId, ratioCopy.fitGenerationArea, workspaceFrameRatio, workspaceId]);

  const workspaceBadgesOverlayStyle = useMemo<React.CSSProperties>(() => ({
    position: 'absolute',
    left: buildLiveScreenX(workspaceFrameRect.x, panOffsetX, zoomLevel),
    top: buildLiveScreenY(workspaceFrameRect.y, panOffsetY, zoomLevel),
    transform: 'translateY(calc(-100% - 8px))',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    pointerEvents: 'none',
    zIndex: 20,
  }), [workspaceFrameRect.x, workspaceFrameRect.y, panOffsetX, panOffsetY, zoomLevel]);
  const workspaceBadgeBaseStyle = useMemo<React.CSSProperties>(() => ({
    padding: '4px 10px',
    backgroundColor: 'rgba(27, 27, 27, 0.88)',
    border: '1px solid #262626',
    color: '#ECECEC',
    fontSize: '12px',
    borderRadius: '9999px',
    whiteSpace: 'nowrap',
  }), []);
  const workspaceNameBadgeStyle = useMemo<React.CSSProperties>(() => ({
    ...workspaceBadgeBaseStyle,
    pointerEvents: 'auto',
    cursor: 'text',
  }), [workspaceBadgeBaseStyle]);
  const workspaceRatioBadgeStyle = useMemo<React.CSSProperties>(() => ({
    ...workspaceBadgeBaseStyle,
    pointerEvents: 'auto',
    cursor: 'pointer',
    color: '#ECECEC',
  }), [workspaceBadgeBaseStyle]);
  const workspaceNameInputStyle = useMemo<React.CSSProperties>(() => ({
    width: `${Math.max(80, workspaceNameDraft.length * 8 + 18)}px`,
    maxWidth: '220px',
    backgroundColor: 'rgba(26, 26, 26, 0.92)',
    color: '#ffffff',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: '4px',
    padding: '2px 6px',
    fontSize: '12px',
    lineHeight: 1.2,
    outline: 'none',
  }), [workspaceNameDraft.length]);

  return (
    <>
      {workspaceOverlayPortalHost && createPortal(
        <div style={workspaceBadgesOverlayStyle}>
          <div
            style={workspaceNameBadgeStyle}
            onClick={handleWorkspaceNameClick}
            onMouseDown={handleWorkspaceBadgeMouseDown}
            title="Click to rename workspace"
          >
            {isEditingWorkspaceName ? (
              <input
                ref={workspaceNameInputRef}
                value={workspaceNameDraft}
                onChange={handleWorkspaceNameInputChange}
                onBlur={handleWorkspaceNameInputBlur}
                onKeyDown={handleWorkspaceNameInputKeyDown}
                onMouseDown={handleWorkspaceNameInputMouseDown}
                style={workspaceNameInputStyle}
              />
            ) : (
              workspaceName
            )}
          </div>
          {observedVersionText && (
            <div style={workspaceBadgeBaseStyle}>
              {observedVersionText}
            </div>
          )}
          <div
            style={workspaceRatioBadgeStyle}
            onClick={openWorkspaceRatioMenu}
            onContextMenu={openWorkspaceRatioMenu}
            onMouseDown={handleWorkspaceBadgeMouseDown}
            title={`${ratioCopy.ratio}: ${workspaceRatioText}`}
          >
            {workspaceRatioText}
          </div>
        </div>,
        workspaceOverlayPortalHost
      )}
      <WorkspaceVersionsPanelContainer
        projectId={projectId}
        workspaceId={workspaceId}
        isActive={isActive}
        composedPlaybackVideoRef={composedPlaybackVideoRef}
        playbackMediaClockHintRef={playbackMediaClockHintRef}
        onComposedPlaybackStateChange={handleComposedPlaybackStateChange}
      />

      <div data-workspace-row-id={workspaceId} style={workspaceFrameStyle}>
        <WorkspaceLoadingOverlay workspaceId={workspaceId} />

        {isComposedPlaybackPreparing && (
          <div
            style={{
              position: 'absolute',
              right: '10px',
              bottom: '10px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '4px',
              zIndex: 11,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                position: 'relative',
                width: '34px',
                height: '34px',
                borderRadius: '9999px',
                backgroundColor: 'rgba(8, 8, 8, 0.72)',
                border: '1px solid rgba(179, 217, 255, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title={(() => {
                const message = composedPlaybackState.prepareMessage;
                if (typeof message === 'string' && message.trim().length > 0) return message;
                return 'Buffering timeline media...';
              })()}
            >
              <span
                className="animate-spin"
                style={{
                  position: 'absolute',
                  inset: '2px',
                  borderRadius: '9999px',
                  border: '2px solid rgba(179, 217, 255, 0.2)',
                  borderTopColor: '#B3D9FF',
                }}
              />
              <span
                style={{
                  position: 'relative',
                  zIndex: 1,
                  color: '#E6F3FF',
                  fontSize: '10px',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1,
                  letterSpacing: '-0.01em',
                }}
              >
                {(() => {
                  const percent = Math.max(0, Math.min(100, Math.round(composedPlaybackState.preparePercent || 0)));
                  return percent > 0 ? `${percent}` : '...';
                })()}
              </span>
            </div>
          </div>
        )}

        {isTimelineCanvasCompositorActive && !isComposedPlaybackPreparing && (
          <WorkspaceTimelineCanvasPreview
            frameRect={workspaceFrameRect}
            tracks={composedPlaybackState.tracks}
            playbackState={composedPlaybackState}
            playbackMediaClockHintRef={playbackMediaClockHintRef}
          />
        )}

        {isBackendComposedPlaybackActive && (
          <video
            ref={composedPlaybackVideoRef}
            src={composedPlaybackState.sourceUrl ?? undefined}
            crossOrigin="anonymous"
            preload="auto"
            playsInline
            muted={composedPlaybackState.isMuted}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              backgroundColor: '#000000',
              pointerEvents: 'none',
              zIndex: 3,
            }}
          />
        )}

        <div style={layersContainerStyle}>
          {isLayerStackMounted && (
            <WorkspaceLayerCanvasStack
              workspaceId={workspaceId}
              workspaceVisible={workspaceVisible}
              workspaceLocked={workspaceLocked}
              editingDrawingTextLayerId={editingDrawingTextLayerId}
              handleLayerMouseDown={handleLayerMouseDown}
              handleResizeHandleMouseDown={handleResizeHandleMouseDown}
              onEditDrawingTextLayer={onEditDrawingTextLayer}
              resizeHandlesPortalHostRef={resizeHandlesPortalHostRef}
              suppressForComposedPlayback={isTimelineCanvasCompositorActive && !isComposedPlaybackPreparing}
            />
          )}
        </div>

        {developPreviewUrl && (
          <img
            src={developPreviewUrl}
            alt=""
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'fill',
              pointerEvents: 'none',
              zIndex: 4,
            }}
          />
        )}

        {isDevelopApplying && (
          <div
            className="loading-anim-bg"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 9,
              borderRadius: 'inherit',
            }}
          />
        )}

        {(workspaceAnnotationsEnabled || maskPenActive || hasInpaintMask) && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 5,
            }}
          >
            <AnnotationCanvas
              projectId={projectId}
              workspaceId={workspaceId}
              isActiveWorkspace={isActive}
              ref={attachAnnotationCanvasRef}
            />
          </div>
        )}
      </div>

      {workspaceVisible && !isNativeLayerCutActive && (
        <CanvasResizeHandles
          workspaceId={workspaceId}
          isActive={isActive}
        />
      )}

      {workspaceVisible && !workspaceLocked && isActive && !isNativeLayerCutActive && (
        <FrameResizeHandles
          workspaceId={workspaceId}
          relativePositioning={false}
        />
      )}

      <div
        ref={resizeHandlesPortalHostRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 40,
          display: isActive ? 'block' : 'none',
        }}
      />
    </>
  );
});

WorkspaceCard.displayName = 'WorkspaceCard';

export default WorkspaceCard;
